import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertCircle } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

import { AdminPage, EmptyState, SaveBar } from '../../../components/design-system/admin';
import { User } from '../../../common/models/user.model';
import { UserGeneralInformation } from '../../../modules/superadmin/edit-user/components/user-general-information.form';
import { UserPasswordReset } from '../../../modules/superadmin/edit-user/components/user-password-reset.card';
import { useUsersApi } from '../../../common/hooks/use-users-api.hook';
import { readApiError } from '../../../common/utils/api-error';

/**
 * Edit an existing user. New users are invited (invite-user.page.tsx), and a
 * password is only ever set by its owner, so neither appears here.
 */
export const EditUser = () => {
    const { t } = useTranslation();
    const [user, setUser] = useState<User>();
    // The user as loaded, so the save bar can say whether anything changed.
    const [loadedUser, setLoadedUser] = useState<User>();
    const [saving, setSaving] = useState(false);
    const { userId } = useParams();
    const [refreshKey, setRefreshKey] = useState(0);
    const [shouldCheckForFormValidation, setShouldCheckForFormValidation] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    const navigate = useNavigate();
    const usersAPI = useUsersApi();

    useEffect(() => {
        loadUser();
        setRefreshKey((key) => key + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    const setUserForEdit = async () => {
        const user = await usersAPI.findOneUser(userId);
        setUser(user);
        setLoadedUser(user);
        return user;
    };

    /** The first load: a failure says so instead of leaving an empty form. */
    const loadUser = async () => {
        setLoadFailed(false);
        try {
            // No user (a deleted id) is a failed load too, not an endless skeleton.
            if (!(await setUserForEdit())) setLoadFailed(true);
        } catch (error) {
            console.error('Error loading user:', error);
            setLoadFailed(true);
        }
    };

    const handleSave = async () => {
        setShouldCheckForFormValidation(true);

        // One validation gate: vendor is required only for a vendor admin. On
        // failure, one toast, and the inline errors stay visible.
        const role = user?.roles?.[0];
        const valid =
            user?.firstname &&
            user?.lastname &&
            user?.email &&
            role &&
            (role === "SUPER_ADMIN" || (role === "VENDOR_ADMIN" && user?.vendor?._id));

        if (!valid) {
            toast.error(t('SUPER_ADMIN.EDIT_USER.VALIDATION_FAILED'));
            return;
        }

        const body: any = { ...user, vendor: user.vendor?._id };

        setSaving(true);
        try {
            await usersAPI.updateUser(body);
            toast.success(t('SUPER_ADMIN.EDIT_USER.USER_UPDATED'));
            await setUserForEdit();
            setShouldCheckForFormValidation(false);
        } catch (error: any) {
            console.error('Error saving user:', error);
            const { code, limit } = await readApiError(error);
            if (code === 'NO_SEATS_LEFT') {
                toast.error(t('SUPER_ADMIN.EDIT_USER.NO_SEATS_LEFT', { limit }));
                return;
            }
            const serverMessage = error?.message && !error.message.startsWith('Request failed')
                ? error.message
                : t('SUPER_ADMIN.EDIT_USER.SAVE_FAILED');
            toast.error(serverMessage);
        } finally {
            setSaving(false);
        }
    };

    const name = [user?.firstname, user?.lastname].filter(Boolean).join(' ');
    const title = name || t('ADMIN_USERS.EDIT_PAGE.TITLE_EDIT');
    const loading = !user;
    const dirty = !loading && JSON.stringify(user) !== JSON.stringify(loadedUser);

    const validationErrors = shouldCheckForFormValidation
        ? [
            !user?.firstname && t('ADMIN_USERS.VALIDATION.FIRST_NAME'),
            !user?.lastname && t('ADMIN_USERS.VALIDATION.LAST_NAME'),
            !user?.email && t('ADMIN_USERS.VALIDATION.EMAIL'),
            user?.roles?.[0] === "VENDOR_ADMIN" && !user?.vendor && t('ADMIN_USERS.VALIDATION.VENDOR'),
        ].filter(Boolean) as string[]
        : [];

    return (
        <AdminPage
            title={title}
            description={user?.email}
            parents={[{ label: t('ADMIN_SHELL.NAV.USERS'), href: '/users' }]}
        >
            <div className="flex max-w-3xl flex-col gap-4">
                {validationErrors.length > 0 && (
                    <div className="flex flex-col gap-2" data-testid="user-validation-errors">
                        {validationErrors.map((message) => (
                            <Alert variant="destructive" key={message}>
                                <AlertCircle />
                                <AlertDescription>{message}</AlertDescription>
                            </Alert>
                        ))}
                    </div>
                )}

                {loadFailed && loading ? (
                    <EmptyState
                        title={t('ADMIN_SHELL.COMMON.LOAD_FAILED')}
                        action={
                            <Button variant="outline" onClick={loadUser}>
                                {t('ADMIN_SHELL.COMMON.RETRY')}
                            </Button>
                        }
                        className="py-24"
                    />
                ) : loading ? (
                    <Skeleton className="h-96 rounded-xl" />
                ) : (
                    <div key={refreshKey} className="flex flex-col gap-4">
                        <UserGeneralInformation user={user} setUser={setUser} check={shouldCheckForFormValidation} />
                        <UserPasswordReset user={loadedUser} />
                    </div>
                )}

                {!loading && (
                    <SaveBar
                        dirty={dirty}
                        saving={saving}
                        onSave={handleSave}
                        saveLabel={t('ADMIN_USERS.EDIT_PAGE.SAVE')}
                        onCancel={() => navigate('/users')}
                        testId="user-save"
                    />
                )}
            </div>
        </AdminPage>
    );
};
